import { isNull } from 'lodash';
import {
  CodPosition,
  CodPositions,
  DRIVE_THRU_1_SCREEN_NAME,
  DRIVE_THRU_2_SCREEN_NAMES,
  DRIVE_THRU_3_SCREEN_NAMES,
  ScreenLayoutTypeFor2Screens,
  ScreenNames,
} from 'src/constants';
import { CodJourney } from 'src/constants/assemblyJourneys';
import {
  GlobalComponentsInterface,
  GlobalComponentsScreenInterface,
  ScreenGlobalComponentInterface,
} from 'src/interfaces';
import {
  isCurrentScreenWithCOD,
  isCurrentScreenWithCODThankYou,
  isCurrentScreenWithDYR,
  isCurrentScreenWithDYS,
} from 'src/utils';

import {
  IPairedItem,
  IScreenLayout,
  UnionGlobalComponent,
} from './ScreenConfigSection.interface';

export const getGlobalComponentsByState = (
  components: GlobalComponentsInterface[] = [],
  screenLayoutComponents: ScreenGlobalComponentInterface[] = [],
  selectedState: string,
) => {
  return components.reduce((acc: UnionGlobalComponent[], comp) => {
    comp.states.forEach((state) => {
      if (state === selectedState) {
        const layoutComponent = screenLayoutComponents.find(
          (c) =>
            c?.componentVariant === comp?.componentVariant &&
            c.componentType === comp.componentType,
        );

        layoutComponent &&
          acc.push({
            ...comp,
            ...layoutComponent,
          } as UnionGlobalComponent);
      }
    });

    return acc;
  }, []);
};

export const mapGlobalComponentsByPaired = (
  globalComponents: UnionGlobalComponent[],
  pairedItemsArr: IPairedItem[][],
) => {
  let globalComponentsCopy = [...globalComponents];
  let pairedComponentsArr: UnionGlobalComponent[][] = [];
  let rest: UnionGlobalComponent[] = [];

  pairedItemsArr.forEach((pairedItems, index) => {
    pairedComponentsArr[index] = [];
    pairedItems.forEach((pairedItem) => {
      const componentIndex = globalComponentsCopy.findIndex((c) => {
        return (
          c.componentType === pairedItem.componentType &&
          c?.componentVariant === pairedItem?.componentVariant
        );
      });

      if (componentIndex !== -1) {
        // paired 1 has few items, paired 2 has few items and so on
        pairedComponentsArr[index].push(globalComponentsCopy[componentIndex]);

        // remove paired component
        globalComponentsCopy.splice(componentIndex, 1);
      }
    });

    if (pairedComponentsArr[index].length === 1) {
      globalComponentsCopy.unshift(pairedComponentsArr[index].splice(0, 1)[0]);
    }
  });

  rest = globalComponentsCopy;

  return { pairedComponentsArr, rest };
};

export const getScreenOptions = (
  totalScreens: number,
  codPosition: CodPosition,
  screenLayoutType: string,
) => {
  let cp = isNull(codPosition) ? CodPositions.LEFT : codPosition;
  let screenNames;
  switch (totalScreens) {
    case 3:
      screenNames = DRIVE_THRU_3_SCREEN_NAMES[cp];
      break;
    case 2:
      screenNames =
        DRIVE_THRU_2_SCREEN_NAMES[cp][
          screenLayoutType as ScreenLayoutTypeFor2Screens
        ];
      break;
    case 1:
      screenNames = DRIVE_THRU_1_SCREEN_NAME;
      break;
    default:
      screenNames = DRIVE_THRU_3_SCREEN_NAMES[cp];
      break;
  }

  return Object.entries(screenNames).map(([screenIndex, screenName]) => ({
    label: screenName,
    value: Number(screenIndex),
    disabled: screenName === ScreenNames.PRESELL, // Presell is not available in current user journey
  }));
};

export const getScreenImageUrl = (
  screenName: ScreenNames,
  screenSequence: number,
  screenLayout: IScreenLayout,
  selectedState: CodJourney,
  codPosition: CodPositions,
) => {
  const images = import.meta.glob(
    '/src/assets/images/layoutScreen/**/*.{png,jpg,jpeg,svg}',
    { eager: true, import: 'default' },
  );

  const load = (path: string) => {
    const fullPath = `/src/assets/images/layoutScreen/${path}`;

    return images[fullPath] as string;
  };
  const driveDirectionPath = codPosition === CodPositions.LEFT ? 'LHD' : 'RHD';
  const loaders = {
    empty: () => load('share/emptyScreen.png'),
    default: () => load('share/defaultScreen.png'),
    dyr: () => load('share/dyr.png'),
    cod: () => load(`${driveDirectionPath}/cod.png`),
    codDys: () => load(`${driveDirectionPath}/codDys.png`),
    codConfirm: () => load('share/codConfirmation.png'),
    codThanks: () => load('share/codThankYou.png'),
  };

  // exception
  if (screenName === ScreenNames.PRESELL) return loaders.empty();
  else if (
    screenName === ScreenNames.MENU &&
    selectedState === CodJourney.CUSTOMER_ENTERS_DRIVE_THRU
  ) {
    return loaders.default();
  }

  const hasCOD = isCurrentScreenWithCOD(
    screenSequence,
    screenLayout as unknown as GlobalComponentsScreenInterface,
  );
  const hasDYS = isCurrentScreenWithDYS(
    screenSequence,
    screenLayout as unknown as GlobalComponentsScreenInterface,
  );
  const hasDYR = isCurrentScreenWithDYR(
    screenSequence,
    screenLayout as unknown as GlobalComponentsScreenInterface,
  );
  const hasThankYou = isCurrentScreenWithCODThankYou(
    screenSequence,
    screenLayout as unknown as GlobalComponentsScreenInterface,
  );

  switch (selectedState) {
    case CodJourney.CUSTOMER_ENTERS_DRIVE_THRU:
      return hasDYR ? loaders.dyr() : loaders.default();

    case CodJourney.ORDER_IS_MADE:
      if (hasDYS) return loaders.codDys();
      else if (hasCOD) return loaders.cod();
      else if (hasDYR) return loaders.dyr();
      else return loaders.default();

    case CodJourney.PURCHASE_IS_CONFIRMED:
      if (hasCOD) return loaders.codConfirm();
      else if (hasThankYou) return loaders.codThanks();
      else return loaders.empty();
  }
};
